import React, { FC, HTMLAttributes, useState, useEffect } from 'react'
import { Box } from '@chakra-ui/core'
import {
  IProductPricedPerCollectionLevel2 as productLevel2,
  IProductPricedPerCollectionLevel3 as productLevel3,
} from '@engyalo/storefront-types/lib/graphql/product'
import { IConfig } from '@engyalo/storefront-types/lib/plugins/model'
import { ProductType } from '@engyalo/storefront-types/lib/graphql/global-types'
import { IProductLevel3 } from '@engyalo/storefront-types/lib/plugins/model'
import { ICustomizationDetail } from './types'
import { RadioCustomization } from './RadioCustomization'
import { CheckCustomization } from './CheckCustomization'
import { CounterCustomization } from './CounterCustomization'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  detail?: productLevel2 | null
  isLoading: boolean
  config: IConfig
  selectedItems: number
  setSelectedItems: (selectedItems: number) => void
  prepareCustomizationSelected: (customizationsDetail: ICustomizationDetail) => void
}

export const CustomizationDetail: FC<IProps> = ({
  detail,
  isLoading,
  config,
  selectedItems,
  setSelectedItems,
  prepareCustomizationSelected,
}: IProps) => {
  const [customization, setCustomization] = useState({} as ICustomizationDetail)

  const { currencyPrefix } = config

  useEffect(() => {
    prepareCustomizationSelected(customization)
  }, [customization, prepareCustomizationSelected])

  const saveCustomization = (data: IProductLevel3, emptyObject = false): void => {
    if (data.id) {
      if (emptyObject) {
        const emptyCustomization: ICustomizationDetail = {}
        emptyCustomization[data.id] = data
        setSelectedItems(0)
        setCustomization({ ...emptyCustomization })
      } else {
        customization[data.id] = data
        setCustomization({ ...customization })
      }
    }
  }

  const setCustomizationSelected = (data: IProductLevel3, emptyObject = false, reduce = false): void => {
    if (detail) {
      const { type, size, maxItems } = detail

      if (type === ProductType.CHECKBOX) {
        if (size && size > 0) {
          if (selectedItems < size) {
            saveCustomization(data, emptyObject)
            setSelectedItems(selectedItems + 1)
          }
        } else {
          if (maxItems && maxItems) {
            if (selectedItems < maxItems) {
              saveCustomization(data, emptyObject)
              setSelectedItems(selectedItems + 1)
            }
          }
        }
      }

      if (type === ProductType.RADIO) {
        saveCustomization(data, emptyObject)
        setSelectedItems(1)
      }

      if (type === ProductType.COUNTER) {
        if (size && size > 0) {
          if (reduce) {
            saveCustomization(data, emptyObject)
            setSelectedItems(selectedItems - 1)
          } else if (selectedItems < size) {
            saveCustomization(data, emptyObject)
            setSelectedItems(selectedItems + 1)
          }
        } else {
          if (maxItems && maxItems) {
            if (reduce) {
              saveCustomization(data, emptyObject)
              setSelectedItems(selectedItems - 1)
            } else if (selectedItems < maxItems) {
              saveCustomization(data, emptyObject)
              setSelectedItems(selectedItems + 1)
            }
          }
        }
      }
    }
  }

  const deleteCustomizationSelected = (data: IProductLevel3): void => {
    if (data.id) {
      delete customization[data.id]
      setCustomization({ ...customization })
      setSelectedItems(selectedItems - 1)
    }
  }

  if (detail) {
    const { id, type: level2Type, customizations: level2Customizations } = detail

    if (id) {
      return (
        <Box>
          {Array.isArray(level2Customizations) &&
            (level2Type === ProductType.RADIO ? (
              <RadioCustomization
                customizations={level2Customizations}
                customizationSelected={customization}
                setCustomizationSelected={setCustomizationSelected}
                currency={currencyPrefix}
              />
            ) : (
              <>
                {level2Customizations.map((level3: productLevel3 | null) => {
                  if (level3 && level3.id) {
                    switch (level2Type) {
                      case ProductType.CHECKBOX:
                        return (
                          <CheckCustomization
                            key={level3.id}
                            data={level3}
                            customizationSelected={customization}
                            setCustomizationSelected={setCustomizationSelected}
                            deleteCustomizationSelected={deleteCustomizationSelected}
                            currency={currencyPrefix}
                          />
                        )

                      case ProductType.COUNTER:
                        return (
                          <CounterCustomization
                            key={level3.id}
                            data={level3}
                            customizationSelected={customization}
                            setCustomizationSelected={setCustomizationSelected}
                            deleteCustomizationSelected={deleteCustomizationSelected}
                            currency={currencyPrefix}
                          />
                        )

                      default:
                        return null
                    }
                  }

                  return null
                })}
              </>
            ))}
        </Box>
      )
    }
  }
  return null
}
